import {
  EditablePropertyData,
  EditableSubjectData,
  SharedEditablePropertyData,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState, SubjectPropertyDataState } from '../../../types.ts';
import { getBumpedSyncState } from '../../utils/getBumpedSyncState.ts';

export const getChangedEntityFromEditableData = <
  P extends SubjectPropertyDataState | CompPropertyDataState,
>(
  current: P,
  editableData: Partial<
    P extends SubjectPropertyDataState ? EditableSubjectData : EditablePropertyData
  >,
): { id: P['id']; changes: P } => {
  const { id } = current;
  const isSubject = id === 'subject';

  const propertyDataKey = isSubject ? 'subjectData' : 'compData';
  const propertyData = isSubject
    ? (current as SubjectPropertyDataState).subjectData
    : (current as CompPropertyDataState).compData;

  const currentEditableData = propertyData.editableData;

  const { improvements, propertyIdentification, site, transaction } = editableData;

  const changedSharedEditableData: SharedEditablePropertyData = {
    improvements: {
      ...currentEditableData.improvements,
      ...improvements,
    },
    propertyIdentification: {
      ...currentEditableData.propertyIdentification,
      ...propertyIdentification,
    },
    site: {
      ...currentEditableData.site,
      ...site,
    },
    transaction: {
      ...currentEditableData.transaction,
      ...transaction,
    },
  };

  const changedEditableData: Partial<EditablePropertyData & EditableSubjectData> =
    changedSharedEditableData;

  if (isSubject) {
    const { opinionOfSiteValue, siteValueCommentary } = editableData as EditableSubjectData;
    const {
      opinionOfSiteValue: currentOpinionOfSiteValue,
      siteValueCommentary: currentSiteValueCommentary,
    } = currentEditableData as EditableSubjectData;

    changedEditableData.opinionOfSiteValue = opinionOfSiteValue ?? currentOpinionOfSiteValue;
    changedEditableData.siteValueCommentary = siteValueCommentary ?? currentSiteValueCommentary;
  } else {
    const { manualAdjustments } = editableData as EditablePropertyData;
    const { manualAdjustments: currentManualAdjustments } =
      currentEditableData as EditablePropertyData;

    changedEditableData.manualAdjustments = {
      ...currentManualAdjustments,
      ...manualAdjustments,
    };
  }

  const changes = {
    id,
    syncState: getBumpedSyncState(current.syncState),
    [propertyDataKey]: {
      ...propertyData,
      editableData: {
        ...currentEditableData,
        ...changedEditableData,
      },
    },
  } as P;

  return { id, changes };
};
